/**
 * RPC (Remote Procedure Call) types for Ditto HTTP API
 *
 * These types define the structure of requests and responses for the Ditto
 * Big Peer HTTP/RPC API, including legacy operations, remote queries, and attachments.
 */

import { DQLError, DQLResult, DQLWarning } from './dql'

/**
 * Callback type for notifications (info, warning, error messages)
 */
export type NotificationCallback = (
  type: 'info' | 'warning' | 'error',
  message: string,
) => void

/**
 * Callback type for error tracking/monitoring
 */
export type ErrorCallback = (
  error: unknown,
  context: Record<string, unknown>,
) => void

/**
 * Optional configuration for RPC client operations
 */
export type RpcClientOptions = {
  /** Callback for UI notifications (replaces toast calls) */
  onNotification?: NotificationCallback
  /** Callback for error tracking/monitoring (replaces Sentry calls) */
  onError?: ErrorCallback
}

// ============================================================================
// Legacy HTTP API Types
// ============================================================================

export type LegacyFindOptions = {
  formatAttachment?: boolean
}

export type LegacySort = {
  property: string
  direction: 'asc' | 'desc'
}

export type LegacyFindParams = {
  query: string
  collection: string
  args?: Record<string, unknown>
  options?: LegacyFindOptions
  sort?: LegacySort[]
  limit?: number
}

export type LegacyHTTPDocument = {
  id: string
  fields: Record<string, unknown>
}

export type LegacyFindResponse = {
  documents: LegacyHTTPDocument[]
  txnId: string
}

export type LegacyFindByIdParams = {
  id: string
} & Pick<LegacyFindParams, 'collection' | 'options'>

export type LegacyFindByIdResponse = {
  txnId: string
} & LegacyHTTPDocument

export type LegacyUpdateParams = {
  query: string
  collection: string
  updates: Array<{
    method?: 'set'
    path: string
    value: unknown
  }>
}

export type LegacyUpsertParams = {
  collection: string
  id: string
  value: Record<string, unknown>
  valueTypeOverrides?: Record<string, string>
}

export type LegacyRemoveParams = Omit<LegacyUpdateParams, 'updates'>

// ============================================================================
// DQL Query Types
// ============================================================================

export type ExecuteDQLParams = {
  args?: Record<string, unknown>
  strictMode?: boolean
}

// ============================================================================
// Remote Query Types
// ============================================================================

export type ExecuteRemoteQueryParams = {
  args?: Record<string, unknown>
  devicePeerKey?: string
}

/**
 * Represents the result from a single peer in a remote query execution
 */
export type RemoteExecutePeerItem = {
  /**
   * Peer metadata
   */
  peer: Record<string, unknown>
  /**
   * Total elapsed time for remote query executed on this peer (in milliseconds)
   */
  elapsedMilliseconds: number
  /**
   * An array of materialized documents resulting from the remote query execution on this peer
   */
  items: DQLResult[]
  /**
   * An error that occurred while executing the query on this peer
   */
  error?: DQLError
  /**
   * An array of warnings generated during query execution on this peer
   */
  warnings: DQLWarning[]
  /**
   * Total number of warnings generated during query execution on this peer
   */
  totalWarningsCount: number
}

export type RemoteQueryHTTPResponse = {
  /**
   * An error that occurred while executing the remote query on multiple peers.
   * This value will always be present, even if there are no errors (as an empty object)
   */
  error: DQLError
  /**
   * An array of results from each peer that was queried
   */
  result: RemoteExecutePeerItem[]
}

// ============================================================================
// Attachment Types
// ============================================================================

export type UploadAttachmentResponse = {
  id: string
  len: number
  metadata?: Record<string, unknown>
}

// ============================================================================
// Auth Webhook Secret Types
// ============================================================================

/**
 * Webhook secret for HMAC-SHA256 signature verification
 */
export type WebhookSecretResponse = {
  /** Base64-encoded webhook secret (128 bytes / 1024 bits) */
  secret: string
  /** Validity start time in RFC 3339 format */
  notBefore: string
  /** Validity end time in RFC 3339 format */
  notAfter: string
  /** RFC 3339 timestamp when the secret was rotated, undefined if still active */
  rotated?: string
}

/**
 * Response containing all webhook secrets for a provider
 */
export type WebhookSecretsListResponse = WebhookSecretResponse[]

// ============================================================================
// RBAC Auth Types
// ============================================================================

/**
 * Error envelope returned by the v4 auth RBAC endpoints
 */
export interface RbacApiError {
  code: string
  message?: string
}

/**
 * Type guard for RbacApiError
 */
export function isRbacApiError(value: unknown): value is RbacApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    typeof (value as RbacApiError).code === 'string'
  )
}

/**
 * Blanket permission covering every collection at once
 */
export type RoleEverythingPermission =
  | 'none'
  | 'read_only'
  | 'write_only'
  | 'read_and_write'

/**
 * One side (read or write) of a collection permission: a blanket allow/deny, or
 * a list of DQL WHERE clauses where any single matching clause grants access
 */
export type RoleCollectionPermissionValueWire = boolean | string[]

export type RoleCollectionPermissionWire = {
  read?: RoleCollectionPermissionValueWire
  write?: RoleCollectionPermissionValueWire
}

/**
 * Either a blanket permission or per-collection permissions keyed by collection name
 */
export type RolePermissionsWire =
  | RoleEverythingPermission
  | Record<string, RoleCollectionPermissionWire>

export type RoleDocIdWire = {
  name: string
  version: string
}

/**
 * A role as read from GET /roles. The document contents are flattened beside
 * `_id` rather than nested, and stay snake_case while the envelope is camelCase.
 */
export type RoleDocWire = {
  _id: RoleDocIdWire
  roles_version: string
  description?: string
  collection_permissions?: RolePermissionsWire
  grant_remote_query?: boolean
}

/**
 * GET /roles keyed by role name, each bucket holding every version of that role
 * in ascending UUIDv7 version order
 */
export type GetRolesBucketedResponseWire = {
  roles: Record<string, RoleDocWire[]>
}

/**
 * GET /roles as a cursor-paginated list of the latest version of each role
 */
export type GetRolesPageResponseWire = {
  roles: RoleDocWire[]
  hasMore: boolean
  cursor?: string
}

export type GetRolesResponseWire =
  | GetRolesBucketedResponseWire
  | GetRolesPageResponseWire

function isRoleDocWire(value: unknown): value is RoleDocWire {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  const doc = value as RoleDocWire
  return (
    typeof doc._id === 'object' &&
    doc._id !== null &&
    typeof doc._id.name === 'string' &&
    typeof doc._id.version === 'string' &&
    typeof doc.roles_version === 'string'
  )
}

/**
 * Type guard accepting either GET /roles wire shape
 */
export function isGetRolesResponse(
  value: unknown,
): value is GetRolesResponseWire {
  if (typeof value !== 'object' || value === null || !('roles' in value)) {
    return false
  }

  const { roles } = value as { roles: unknown }

  if (Array.isArray(roles)) {
    return (
      typeof (value as GetRolesPageResponseWire).hasMore === 'boolean' &&
      roles.every(isRoleDocWire)
    )
  }

  if (typeof roles !== 'object' || roles === null) {
    return false
  }

  return Object.values(roles).every(
    (bucket) => Array.isArray(bucket) && bucket.every(isRoleDocWire),
  )
}

/**
 * One side (read or write) of a collection permission, with the wire default applied
 */
export type RoleCollectionPermissionValue = boolean | string[]

export type RoleCollectionPermission = {
  read: RoleCollectionPermissionValue
  write: RoleCollectionPermissionValue
}

export type RolePermissions =
  | RoleEverythingPermission
  | Record<string, RoleCollectionPermission>

/**
 * A role as the Portal consumes it, with every wire default already resolved
 */
export type Role = {
  name: string
  version: string
  description: string
  collectionPermissions: RolePermissions
  grantRemoteQuery: boolean
}

/**
 * A page of roles, sorted by role name
 */
export type RolesPage = {
  roles: Role[]
  hasMore: boolean
  cursor?: string
}

/**
 * The document contents nested under `doc` in POST /roles, snake_case inside
 * a camelCase envelope. `deny_unknown_fields` applies only to the envelope: a
 * camelCased key here is not rejected, it is silently dropped and the field
 * falls back to its default.
 */
export type RoleDocContentsWire = {
  roles_version: 'v1-preview'
  description: string
  collection_permissions: RolePermissionsWire
  grant_remote_query: boolean
}

/**
 * Request body for POST /roles. `version` is deliberately absent: the server
 * assigns it.
 */
export type AddRoleRequestWire = {
  name: string
  doc: RoleDocContentsWire
}

/**
 * Portal-side input for creating or replacing a role. `roles_version` is a
 * schema constant supplied at the transport boundary, not data, so it has no
 * place here.
 */
export type CreateRoleInput = {
  name: string
  description: string
  collectionPermissions: RolePermissions
  grantRemoteQuery: boolean
}

/**
 * A user as read from GET /auth/users. `userId` and `roles` always arrive,
 * `roles` as an empty array when the user holds none; the rest are omitted
 * rather than sent as null.
 */
export type UserDocWire = {
  userId: string
  roles: string[]
  identityVersion?: string
  identityServiceMetadata?: Record<string, unknown>
}

/**
 * GET /auth/users as a cursor-paginated list, sorted ascending by `userId`.
 * `cursor` is derived from the last id on the page.
 */
export type GetUsersResponseWire = {
  users: UserDocWire[]
  hasMore: boolean
  cursor?: string
}

/**
 * Query parameters for GET /auth/users.
 */
export type GetUsersParams = {
  userId?: string
  cursor?: string
  limit?: number
}

/**
 * Request body for PATCH /auth/users/{user_id}. `roles` replaces the whole set.
 */
export type UpdateUserRolesRequestWire = {
  roles: string[]
}

/**
 * Response to PATCH /auth/users/{user_id}. `transactionId` is the user's
 * transaction id after the write, which on a no-op is the id it already had.
 */
export type UpdateUserRolesResponse = {
  identityVersion: string
  transactionId: number
}

/**
 * A user as the Portal consumes it, with every wire default already resolved
 */
export type User = {
  userId: string
  roles: string[]
  identityVersion?: string
  /** Whether the user has an identity version, without which they cannot sign in. */
  isProvisioned: boolean
}

/**
 * A page of users, sorted by user id
 */
export type UsersPage = {
  users: User[]
  hasMore: boolean
  cursor?: string
}

/**
 * Parameters for managing webhook secrets (get, delete)
 */
export type WebhookSecretParams = {
  /** The provider name (webhook name) */
  providerName: string
}

/**
 * Parameters for generating a webhook secret
 */
export type GenerateWebhookSecretParams = {
  /** The provider name (webhook name) */
  providerName: string
  /** Validity end time in ISO 8601 format */
  notAfter: string
}

/**
 * Parameters for rotating a webhook secret (marks old as rotated, generates new)
 */
export type RotateWebhookSecretParams = {
  /** The provider name (webhook name) */
  providerName: string
  /** The secret to rotate (will be marked as rotated) */
  rotate: {
    secret: string
    notBefore: string
    notAfter: string
  }
  /** Validity period for the new secret */
  new: {
    notBefore: string
    notAfter: string
  }
}

// ============================================================================
// Type Guards
// ============================================================================

export function isLegacyFindResponse<T>(
  response: T | LegacyFindResponse,
): response is LegacyFindResponse {
  return !!response && typeof response === 'object' && 'documents' in response
}

export function isLegacyFindByIdResponse<T>(
  response: T | LegacyFindByIdResponse,
): response is LegacyFindByIdResponse {
  // Note: having a valid base without fields is indicative of a non-existent document
  const isValidBase =
    !!response && typeof response === 'object' && 'txnId' in response
  if (isValidBase && 'fields' in response) {
    return (
      'id' in response &&
      typeof response.id === 'string' &&
      typeof response.fields === 'object'
    )
  }
  return isValidBase
}

/**
 * A utility function to assert that a response is a RemoteQueryHTTPResponse. This enforces
 * the structure of the response object, ensuring all of the *required* fields are present
 * and of the correct type.
 */
export function isRemoteQueryHTTPResponse(
  response: unknown,
): response is RemoteQueryHTTPResponse {
  if (!response || typeof response !== 'object') {
    return false
  }

  const r = response as Record<string, unknown>

  // Both error and result must be present
  if (!('error' in r) || !('result' in r)) {
    return false
  }

  // error must be an object (not null, since typeof null === 'object')
  if (typeof r.error !== 'object' || r.error === null) {
    return false
  }

  // result must be an array
  if (!Array.isArray(r.result)) {
    return false
  }

  return true
}
